/*

  Board.ts

  Board component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { Component, ComponentFunc, m, VnodeAttrs, VnodeChildren } from "./mithril";
import { mt, ts } from "./i18n";
import { ERROR_MESSAGES, IBaseGame, IGame, IView } from "./types";
import { addPinchZoom, glyph } from "./util";
import { Buttons } from "./buttons";
import { TileSquare, ReviewTileSquare, DropTargetSquare, Rack } from "./rack";

const GameOver: Component<{ game: IGame }> = {
  view: (vnode) => {
    // Show message at end of game, either congratulating a win or
    // solemnly informing the player that the game is over
    const { game } = vnode.attrs;
    if (game.congratulate) {
      return m("div", { id: "congrats" },
        [
          glyph("bookmark"),
          " ",
          mt("strong", "Til hamingju með sigurinn!")
        ]
      );
    }
    if (game.over) {
      return m("div", { id: "gameover" },
        [
          glyph("info-sign"),
          " ",
          mt("strong", "Viðureigninni er lokið")
        ]
      );
    }
    return undefined;
  }
};

const Errors: Component<{ game: IBaseGame }> = {
  view: (vnode) => {
    // Error messages, selectively displayed
    const { game } = vnode.attrs;
    const err = game.currentError || "";
    if (err && err in ERROR_MESSAGES) {
      const msg: string = game.currentMessage || "";
      const txt: string = ts(ERROR_MESSAGES[err]);
      const wix = txt.indexOf("{word}");
      let children: VnodeChildren[];
      if (wix >= 0) {
        // Found {word} macro: create three child nodes
        children = [ txt.slice(0, wix), m("span.errword", msg), txt.slice(wix + 6) ];
      }
      else {
        // No {word} macro: just return the message as-is
        children = [ txt ];
      }
      return m(".error",
        {
          style: { visibility: "visible" },
          onclick: (ev: Event) => { game.resetError(); ev.preventDefault(); }
        },
        [ glyph("exclamation-sign"), ...children ]
      );
    }
    return undefined;
  }
};

interface IAttributes {
  view: IView;
  game: IGame;
}

export const BoardArea: Component<IAttributes> = {
  // Collection of components in the board (left-side) area
  view: (vnode) => {
    const { view, game } = vnode.attrs;
    let r: VnodeChildren = [];
    if (game) {
      r = [
        m(Board, { view, game, review: false }),
        m(Rack, { view, game, review: false }),
        m(Buttons, { view }),
        m(Errors, { game }),
        m(GameOver, { game }),
      ];
      r = r.concat(view.vwDialogs());
    }
    return m(".board-area", r);
  }
};

interface IBoardAttributes {
  view: IView;
  game: IBaseGame;
  review: boolean;
}

export const Board: ComponentFunc<IBoardAttributes> = (initialVnode) => {
  // The game board, a 15x15 table plus row (A-O) and column (1-15) identifiers

  const { view, game, review } = initialVnode.attrs;

  function colid(): VnodeChildren {
    // The column identifier row
    let r: VnodeChildren = [];
    r.push(m("td"));
    for (let col = 1; col <= 15; col++)
      r.push(m("td", col.toString()));
    return m("tr.colid", r);
  }

  function row(rowid: string): VnodeChildren {
    // Each row of the board
    let r: VnodeChildren = [];
    r.push(m("td.rowid", { key: "R" + rowid }, rowid));
    for (let col = 1; col <= 15; col++) {
      const coord = rowid + col.toString();
      if (game && (coord in game.tiles))
        // There is a tile in this square: render it
        r.push(m(TileSquare, { view, game, key: coord, coord: coord, opponent: false }));
      else if (review)
        // Empty, inert square
        r.push(m(ReviewTileSquare, { view, game, key: coord, coord: coord, opponent: false }));
      else
        // Empty square which is a drop target
        r.push(m(DropTargetSquare, { view, game, key: coord, coord: coord }));
    }
    return m("tr", r);
  }

  function allrows(): VnodeChildren {
    // Return a list of all rows on the board
    let r: VnodeChildren = [];
    r.push(colid());
    const rows = "ABCDEFGHIJKLMNO";
    for (const rw of rows)
      r.push(row(rw));
    return r;
  }

  return {
    view: (vnode) => {
      // const scale = view.boardScale || 1.0;
      let attrs: VnodeAttrs = {};
      // Add handlers for pinch zoom functionality
      // Note: resist the temptation to pass zoomIn/zoomOut directly,
      // as that would not bind the 'this' pointer correctly
      addPinchZoom(attrs, () => view.zoomIn(), () => view.zoomOut());
      /*
      if (scale !== 1.0)
        attrs.style = `transform: scale(${scale})`;
      */
      return m(".board",
        { id: "board-parent" },
        m("table.board", attrs, m("tbody", allrows()))
      );
    }
  };
}
